(() => {
  if (customElements.get('hd-header')) {
    return;
  }

  class HdHeader extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.updateScrolledState.bind(this);
      this.onOpenDrawer = this.openDrawer.bind(this);
      this.onCloseDrawer = this.closeDrawer.bind(this);
      this.onKeyup = this.handleKeyup.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onBackdropClick = this.closeDrawer.bind(this);
      this.onPointerOver = this.handlePointerOver.bind(this);
      this.onPointerOut = this.handlePointerOut.bind(this);
      this.onFocusIn = this.handleFocusIn.bind(this);
      this.onFocusOut = this.handleFocusOut.bind(this);
      this.onDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
      this.onAccountToggle = this.handleAccountToggle.bind(this);
    }

    connectedCallback() {
      this.drawer = this.querySelector('[data-hd-header-drawer]');
      this.openButton = this.querySelector('[data-hd-header-open-drawer]');
      this.closeButton = this.querySelector('[data-hd-header-close-drawer]');
      this.backdrop = this.querySelector('[data-hd-header-drawer-backdrop]');
      this.submenuItems = Array.from(this.querySelectorAll('[data-hd-header-submenu-item]'));
      this.accountDetails = this.querySelector('[data-hd-header-account]');
      this.accountTrigger = this.querySelector('[data-hd-header-account-trigger]');
      this.classList.add('is-submenu-ready');

      this.openButton?.addEventListener('click', this.onOpenDrawer);
      this.closeButton?.addEventListener('click', this.onCloseDrawer);
      this.backdrop?.addEventListener('click', this.onBackdropClick);
      document.addEventListener('keyup', this.onKeyup);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('pointerover', this.onPointerOver);
      this.addEventListener('pointerout', this.onPointerOut);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      this.accountDetails?.addEventListener('toggle', this.onAccountToggle);

      this.updateScrolledState();
    }

    disconnectedCallback() {
      this.openButton?.removeEventListener('click', this.onOpenDrawer);
      this.closeButton?.removeEventListener('click', this.onCloseDrawer);
      this.backdrop?.removeEventListener('click', this.onBackdropClick);
      document.removeEventListener('keyup', this.onKeyup);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('pointerover', this.onPointerOver);
      this.removeEventListener('pointerout', this.onPointerOut);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      this.accountDetails?.removeEventListener('toggle', this.onAccountToggle);
      this.classList.remove('is-submenu-ready');
    }

    updateScrolledState() {
      this.classList.toggle('is-scrolled', window.scrollY > 8);
    }

    openDrawer() {
      this.classList.add('is-drawer-open');
      this.openButton?.setAttribute('aria-expanded', 'true');
      this.drawer?.removeAttribute('aria-hidden');
      this.closeButton?.focus();
    }

    closeDrawer() {
      if (!this.classList.contains('is-drawer-open')) {
        return;
      }

      this.classList.remove('is-drawer-open');
      this.openButton?.setAttribute('aria-expanded', 'false');
      this.drawer?.setAttribute('aria-hidden', 'true');
      this.openButton?.focus();
    }

    getSubmenuItem(target) {
      const item = target instanceof Element ? target.closest('[data-hd-header-submenu-item]') : null;
      return item && this.contains(item) ? item : null;
    }

    openSubmenu(item) {
      if (!item) {
        return;
      }

      this.closeSubmenus(item);
      item.classList.add('is-submenu-open');
      item.querySelector('[data-hd-header-submenu-trigger]')?.setAttribute('aria-expanded', 'true');
    }

    closeSubmenu(item) {
      item?.classList.remove('is-submenu-open');
      item?.querySelector('[data-hd-header-submenu-trigger]')?.setAttribute('aria-expanded', 'false');
    }

    closeSubmenus(exceptItem = null) {
      this.submenuItems?.forEach((item) => {
        if (item !== exceptItem) {
          this.closeSubmenu(item);
        }
      });
    }

    handleAccountToggle() {
      this.accountTrigger?.setAttribute('aria-expanded', this.accountDetails?.open ? 'true' : 'false');
      if (this.accountDetails?.open) {
        this.closeSubmenus();
      }
    }

    closeAccount(restoreFocus = false) {
      if (!this.accountDetails?.open) {
        return false;
      }

      this.accountDetails.open = false;
      if (restoreFocus) {
        this.accountTrigger?.focus();
      }
      return true;
    }

    handlePointerOver(event) {
      const item = this.getSubmenuItem(event.target);
      if (item && !item.contains(event.relatedTarget)) {
        this.openSubmenu(item);
      }
    }

    handlePointerOut(event) {
      const item = this.getSubmenuItem(event.target);
      if (item && !item.contains(event.relatedTarget) && !item.contains(document.activeElement)) {
        this.closeSubmenu(item);
      }
    }

    handleFocusIn(event) {
      this.openSubmenu(this.getSubmenuItem(event.target));
    }

    handleFocusOut(event) {
      const item = this.getSubmenuItem(event.target);
      if (item && !item.contains(event.relatedTarget)) {
        this.closeSubmenu(item);
      }
    }

    handleDocumentPointerDown(event) {
      if (!this.getSubmenuItem(event.target)) {
        this.closeSubmenus();
      }
      if (this.accountDetails && !this.accountDetails.contains(event.target)) {
        this.closeAccount();
      }
    }

    handleKeyup(event) {
      if (event.key === 'Escape') {
        if (this.closeAccount(true)) {
          return;
        }

        const openItem = this.submenuItems?.find((item) => item.classList.contains('is-submenu-open'));
        if (openItem) {
          const trigger = openItem.querySelector('[data-hd-header-submenu-trigger]');
          this.closeSubmenu(openItem);
          trigger?.focus();
          return;
        }

        this.closeDrawer();
      }
    }

    handleKeydown(event) {
      if (event.key !== 'Tab' || !this.classList.contains('is-drawer-open') || !this.drawer) {
        return;
      }

      const focusableElements = Array.from(
        this.drawer.querySelectorAll('a[href], button:not([disabled]), summary, details, [tabindex]:not([tabindex="-1"])')
      ).filter((element) => element.offsetParent !== null);

      if (!focusableElements.length) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  customElements.define('hd-header', HdHeader);
})();
